/**
 * VerdictReasonPanel — 「不认同」之后的否决原因采集（审查发现卡片与对话回复
 * 页脚共用）。
 *
 * 交互口径（docs/design/2026-08-20-AI结论表态与反馈设计.md 2.3）：否决在点击
 * 那一刻就已落库，本面板采集的分类与自由文本**都是选填**，不填直接走开也算
 * 完成否决。AI 误判率可能不低、律师要否决的条目不少，任何必填项都会把表态从
 * 顺手一点变成逐条填表，实际结果是律师干脆不表态，一条数据也收不到。
 *
 * 因此本组件不做任何校验、不阻塞、不提示"请选择原因"，也没有提交按钮：选中
 * 分类或输入框失焦即补记到同一条表态。
 *
 * 两种形态：
 * - 折叠态 {@link VerdictReasonSummary}：显示已填原因（或"未填写否决原因"），
 *   附一个展开入口。
 * - 展开态（本组件默认导出）：七项分类 chip 一屏可见（词表刻意短，见 2.3）
 *   加一个补充说明输入框，以及一个「跳过」出口。
 */

import { useCallback, useState } from "react";
import { Input } from "antd";
import { AI_VERDICT_REASON_OPTIONS, formatVerdictReason } from "../../constants";
import type { AiVerdictReasonCode } from "../../types/aiReview";
import styles from "../../styles/verdict.module.css";

export interface VerdictReasonPayload {
  reasonCode?: AiVerdictReasonCode | null;
  reasonText?: string | null;
}

/** data-testid 拼装：`${prefix}-reason-<部件>${suffix}`。既有审查侧用例依赖
 *  `finding-reason-*`，故 prefix 默认 `finding`。 */
function testId(prefix: string, part: string, suffix?: string): string {
  return `${prefix}-reason-${part}${suffix ? `-${suffix}` : ""}`;
}

/** 折叠态：一行摘要 + 展开入口。原因为空时明说"未填写"——留白会让人以为
 *  这里本来就没有可填的东西。 */
export function VerdictReasonSummary({
  reasonCode,
  reasonText,
  onOpen,
  testIdPrefix = "finding",
  idSuffix,
}: {
  reasonCode?: AiVerdictReasonCode | null;
  reasonText?: string | null;
  onOpen: () => void;
  testIdPrefix?: string;
  idSuffix?: string;
}) {
  const summary = [formatVerdictReason(reasonCode), reasonText ?? ""]
    .filter(Boolean)
    .join("：");
  return (
    <div className={styles.reasonSummary}>
      <span data-testid={testId(testIdPrefix, "summary", idSuffix)}>
        {summary ? `否决原因：${summary}` : "未填写否决原因"}
      </span>
      <button
        type="button"
        className={styles.linkBtn}
        onClick={onOpen}
        data-testid={testId(testIdPrefix, "open", idSuffix)}
      >
        {summary ? "修改原因" : "补充原因（选填）"}
      </button>
    </div>
  );
}

export default function VerdictReasonPanel({
  reasonCode,
  reasonText,
  pending,
  onChange,
  onSkip,
  testIdPrefix = "finding",
  idSuffix,
}: {
  reasonCode?: AiVerdictReasonCode | null;
  reasonText?: string | null;
  /** 该条目有在途的表态请求：分类禁用，避免连点产生互相覆盖的写入。 */
  pending?: boolean;
  /** 补记原因到同一条表态。分类与文本一并上抛，调用方据此整条覆盖。 */
  onChange: (payload: VerdictReasonPayload) => void;
  onSkip: () => void;
  testIdPrefix?: string;
  idSuffix?: string;
}) {
  const [textDraft, setTextDraft] = useState(reasonText ?? "");

  // 服务端回填的理由变化时同步草稿（改判、重新拉取后显示的是库里的那份，不是
  // 上一条留下的残影）。在渲染期比对上一次的 prop 值并直接调整 state——这是
  // React 认可的派生状态写法，比 useEffect 少一次级联渲染。
  const [syncedText, setSyncedText] = useState(reasonText ?? "");
  if (syncedText !== (reasonText ?? "")) {
    setSyncedText(reasonText ?? "");
    setTextDraft(reasonText ?? "");
  }

  const handlePickReason = useCallback(
    (code: AiVerdictReasonCode) => {
      // 再点一次同一枚 = 取消选择：分类是选填项，必须能取消，否则误点无法回退。
      const next = reasonCode === code ? null : code;
      onChange({ reasonCode: next, reasonText: textDraft || null });
    },
    [onChange, reasonCode, textDraft],
  );

  const handleTextBlur = useCallback(() => {
    const next = textDraft.trim();
    if (next === (reasonText ?? "").trim()) return;
    onChange({ reasonCode: reasonCode ?? null, reasonText: next || null });
  }, [textDraft, reasonText, reasonCode, onChange]);

  return (
    <div
      className={styles.reasonPanel}
      data-testid={testId(testIdPrefix, "panel", idSuffix)}
    >
      <div className={styles.reasonHint}>
        <span>否决原因（选填，不填也已完成否决）</span>
        <button
          type="button"
          className={styles.linkBtn}
          onClick={onSkip}
          data-testid={testId(testIdPrefix, "skip", idSuffix)}
        >
          跳过
        </button>
      </div>
      <div className={styles.reasonChips}>
        {AI_VERDICT_REASON_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`${styles.reasonChip} ${
              reasonCode === opt.value ? styles.reasonChipActive : ""
            }`}
            disabled={pending}
            onClick={() => handlePickReason(opt.value)}
            data-testid={testId(testIdPrefix, opt.value, idSuffix)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <Input.TextArea
        size="small"
        autoSize={{ minRows: 1, maxRows: 3 }}
        placeholder="补充说明（选填）"
        value={textDraft}
        onChange={(e) => setTextDraft(e.target.value)}
        onBlur={handleTextBlur}
        maxLength={2000}
        data-testid={testId(testIdPrefix, "text", idSuffix)}
      />
    </div>
  );
}
